/* eslint-disable no-underscore-dangle */
import * as React from 'react';
import axios from 'axios';
import update from 'immutability-helper';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Store from '../../../redux/store';
import { SET_FLAGS } from '../../../redux/actionTypes';
import SelectInput from '../../_common/form/select-input';
import DatasetSearch from '../../dataset-search';
import DataFiltersDropdown from '../../data-controls-panel/data-filters-dropdown';
import DataSort from '../../data-sort';
import DataFilter from '../../data-filter';
import LozengeFilterOptions from '../../data-controls-panel/lozenge-filter-options';

import QST from '../../../utilities/query-string-manager';
import APIManager from '../../../utilities/api-manager';
import HistoryInstance from '../../../utilities/history';
import { get, getText, getConfig, sortDesc, sortAsc, sortDescByDate, sortByEnhanced, debounce, authdata, createSearchStrategyFactory, SearchType, validInternalUser } from '../../../helpers';
import { mapSearchResultToDataProfileCard, mapSearchResultsToQuery, mapSearchResultToSolution, mapSearchResultToBluePrint } from '../../../utilities/data-mappers';
import safeParse from '../../../utilities/safe-parse';
import SPGVariables from '../../../utilities/spg-variables';
import { sortQLAsc, sortQLDesc } from '../../../helpers/sort';

const filterOptions = getConfig('components.filterOptions');
const Window = window as AnonymousObject;
Window.dataLayer = Window.dataLayer || [];
let defaultSort = getConfig('components.tile-grid.defaultSort');
const defaultSortQueryLibrary = getConfig('components.tile-grid.defaultSort_querylibrary');
const defaultSortAllPages = getConfig('components.tile-grid.defaultSort');
const sortBy = getText('generic.sortBy');
const debounceDuration = getConfig('components.typeaheadThrottleDuration');
const spgVariables = SPGVariables();
const env = spgVariables.ENV;

const initialState: SearchProviderState = {
    sort: defaultSort,
    search: '',
    filters: [],
    filterString: [],
    filterCount: 0,
    list: [],
    listLength: 0,
    status: 'initial-load'
};

const initialResponse = {
    selectComponent: null,
    searchComponent: null,
    filterComponent: null,
    lozengeFilterComponent: null,
    mobileSelectComponent: null,
    mobileFilterComponent: null,
    sort: '',
    search: '',
    isSearching: false,
    list: [],
    listLength: 0,
    filterCount: 0,
    status: '',
    hasGenAISearchError: false
};

const sortedList = (sort: string, type: SearchProductType, list: (DataProfileCard | APISolution)[] | Query[]) => {
    if (!list.length) return list;
    if (sort === 'recently-added' && type === 'querylibrary') return sortDescByDate(list, 'versiondate');
    if (sort === 'dataset-asc' && type === 'querylibrary') return sortQLAsc(list, 'datasets');
    if (sort === 'dataset-desc' && type === 'querylibrary') return sortQLDesc(list, 'datasets');
    if (sort === 'recently-added' && (type === 'dataSets' || type === 'combinedProducts' || type === 'solutions')) return sortDescByDate(list, 'addedDate');
    if (sort === 'dataset-desc' || sort === 'name-desc' || sort === 'solution-desc' || sort === 'query-desc') return sortDesc(list, 'name');
    if (sort === 'dataset-asc' || sort === 'name-asc' || sort === 'solution-asc' || sort === 'query-asc') return sortAsc(list, 'name');
    if (sort === 'vendor-desc') return sortDesc(list, 'dataProvider', 'name');
    if (sort === 'vendor-asc') return sortAsc(list, 'dataProvider', 'name');
    if (sort === 'recently-enhanced') return sortByEnhanced(list);
    if (sort === 'relevance') {
        return list.sort((a: AnonymousObject, b: AnonymousObject) => {
            if (a.sortIndex < b.sortIndex) return -1;
            if (a.sortIndex > b.sortIndex) return 1;
            return 0;
        });
    }
    return list;
};

const parseFilters = (filters: FilterObjects[]) => {
    let filterCount = 0;
    const filterArray: string[] = [];
    Object.entries(filters).forEach((filter: AnonymousObject) => {
        const filterKeyColumn = filter[1].filterKey as string;
        const options: FilterCheckBoxItem[] = filter[1].items;
        options.forEach(option => {
            if (option.checked) {
                if (option.value instanceof Array) {
                    filterArray.push(...option.value);
                } else {
                    const header = filterOptions[filterKeyColumn];
                    filterArray.push(`${header.keyColumn}:${option.value}`);
                }
                ++filterCount;
            }
        });
    });
    return {
        filterArray,
        filterCount
    };
};

const hydrateStateFromUrl = () => {
    const newState: AnonymousObject = {};

    const deepLinkedSearch = QST.getSearch();

    if (deepLinkedSearch.search) newState.search = deepLinkedSearch.search as string;

    if (deepLinkedSearch.sort) newState.sort = deepLinkedSearch.sort as string;

    if (deepLinkedSearch.filters) {
        const filterQuery = (deepLinkedSearch.filters as string) || '';
        const parsedFilterQuery = safeParse(filterQuery || '{}') || [];
        newState.filters = [...initialState.filters].map((filter: AnonymousObject) => {
            const hasMatch = !!parsedFilterQuery[filter.filterKey];
            if (hasMatch) {
                // if this filter is found in the filter query lets parse it
                return {
                    ...filter,
                    items: filter.items.map((item: FilterCheckBoxItem) => ({
                        ...item,
                        checked: parsedFilterQuery[filter.filterKey].includes(item.name)
                    }))
                };
            }
            return { ...filter };
        });
    }

    return newState;
};

const selectComponent = (type: SearchProductType, controlledValue: string, isDisabled: boolean, onChange: (name: string, value: string) => void) => {
    const defaultSortOptions = getText(`pages.${type}.sortOptions`);
    defaultSort = useLocation().pathname.includes('query-library') ? defaultSortQueryLibrary : defaultSortAllPages;

    return (
        <SelectInput
            label={sortBy}
            name="sort"
            options={defaultSortOptions}
            controlledValue={controlledValue}
            isDisabled={isDisabled}
            onChange={onChange}
        />
    );
};

const getActiveTab = (type: SearchProductType, activeTab: number) => {
    if (type === 'combinedProducts' && activeTab !== 0) return activeTab;
    if (type === 'dataSets') return 1;
    if (type === 'solutions') return 2;
    if (type === 'blueprints') return 3;
    if (type === 'querylibrary') return 4;
    return 0;
};

const searchComponent = (type: SearchProductType, activeTab: number, lists: (DataProfileCard | APISolution | APIBlueprint)[] | Query[], searchValue: string, isSearching: boolean, updateSearchValue: (value: string) => void, onFilterChanged: (selTab: number) => void, urlPath: string, isLLMSearch: boolean, handleSearchSubmit: (isSubmit: boolean) => void, isLLMSubmitted: boolean, genAIResponse: any) => {
    const placeholder = getText(`pages.${type}.searchPlaceholder`);
    const searchResults = getText(`pages.${type}.searchResultsSummary`);
    const user = useSelector((rootState: RootStoreState) => get(rootState, 'user', ''));
    const userEmail = user && user.userInfo && user.userInfo.Email ? user.userInfo.Email : '';
    const authLists = authdata(lists as (APISolution)[], userEmail);
    const hideFilterFlag = isLLMSubmitted || (isLLMSearch && genAIResponse);
    return (
        <DatasetSearch
            placeholder={placeholder}
            searchResults={searchResults}
            searchValue={searchValue}
            isSearching={isSearching}
            updateSearchValue={updateSearchValue}
            lists={authLists.length < lists.length ? authLists : lists as (DataProfileCard | APISolution | APIBlueprint)[]}
            activeTab={getActiveTab(type, activeTab)}
            onFilterChanged={onFilterChanged}
            urlPath={urlPath}
            isLLMSearch={isLLMSearch}
            handleSearchSubmit={handleSearchSubmit}
            hideFilter={hideFilterFlag}
        />
    );
};

const filterComponent = (
    filters: FilterObjects[],
    clearAll: () => void,
    isFiltersActive: boolean,
    updateFilter: (filterItems: FilterCheckBoxItem[], filterKey: string) => void
) => <DataFiltersDropdown filters={filters} handleClearAll={clearAll} isFiltersActive={isFiltersActive} onUpdate={updateFilter} />;

const lozengeFilterComponent = (filters: FilterObjects[], updateFilter: (filterItems: FilterObjects[]) => void) => (
    <LozengeFilterOptions filters={filters} onUpdate={updateFilter} />
);

const mobileSelectComponent = (type: SearchProductType, controlledValue: string, isDisabled: boolean, onChange: (name: string, value: string) => void) => {
    const defaultSortOptions = getText(`pages.${type}.sortOptions`);
    return <DataSort queryString={controlledValue} updateQueryString={onChange} sortOptions={defaultSortOptions} disabled={isDisabled} />;
};

const mobileFilterComponent = (
    filters: FilterObjects[],
    clearAll: () => void,
    filterCount: number,
    applyFilters: (filters: FilterObjects[]) => void
) => <DataFilter filters={filters} handleClearAll={clearAll} applyFilters={applyFilters} filterCount={filterCount} />;

const updateSearchUrl = debounce((search, dispatch) => {
    const currentSearch = QST.getSearchItem('search');
    if (currentSearch !== search) QST.setSearchItem({ search });
    dispatch();
}, debounceDuration);

const updateSortUrl = (sort: string) => {
    QST.setSearchItem({ sort }, true);
};

const updateFilterURL = (filterItems: FilterCheckBoxItem[], filterKey: string) => {
    const oldFilters = (QST.getSearchItem('filters') as string) || '';
    const parsedOldFilters = safeParse(oldFilters || '{}') || [];
    // get array of options as names
    const optionArray = filterItems.filter(item => item.checked).map(item => item.name);
    // if array has length add to the filters / otherwise delete
    if (optionArray.length) parsedOldFilters[filterKey] = optionArray;
    else delete parsedOldFilters[filterKey];
    // convert to string - remove if string === {}
    const filtersAsString = JSON.stringify(parsedOldFilters);
    // finally update the state
    if (filtersAsString !== '{}') QST.setSearchItem({ filters: filtersAsString });
    else QST.removeSearchItem('filters');
};

const updateFiltersURL = (filters: FilterObjects[]) => {
    const updatedFilters: AnonymousObject = {};
    filters.forEach((filter: FilterObjects) => {
        const optionArray = filter.items.filter((item: FilterCheckBoxItem) => item.checked).map((item: FilterCheckBoxItem) => item.name);
        if (optionArray.length) updatedFilters[filter.filterKey] = optionArray;
    });
    // convert to string - remove if string === {}
    const filtersAsString = JSON.stringify(updatedFilters);
    // finally update the state
    if (filtersAsString !== '{}') QST.setSearchItem({ filters: filtersAsString });
    else QST.removeSearchItem('filters');
};

const addIndex = (arr: AnonymousObject) => {
    return arr.map((item: DataProfileCard | APISolution | Query, index: number) => {
        item.sortIndex = index;
        return item;
    });
};

const reducer: React.Reducer<SearchProviderState, SearchProviderAction> = (state, action) => {
    switch (action.type) {
        case 'update-search': {
            const search = action.payload;
            return {
                ...state,
                search,
                status: 'updated-search'
            };
        }
        case 'update-search-debounced': {
            return {
                ...state,
                status: 'updated-search-debounced'
            };
        }
        case 'update-sort':
            return {
                ...state,
                sort: action.payload
            };
        case 'update-filter': {
            const index = state.filters.findIndex((filter: FilterObjects) => filter.filterKey === action.payload.filterKey);
            const newState = update(state, { filters: { [index]: { items: { $set: action.payload.filterItems } } } });
            const parsedFilters = parseFilters(newState.filters);
            newState.filterString = parsedFilters.filterArray;
            newState.filterCount = parsedFilters.filterCount;
            newState.status = 'updated-filters';
            return newState;
        }
        case 'update-filters': {
            const parsedFilters = parseFilters(action.payload);
            return update(state, {
                filters: { $set: action.payload },
                filterString: { $set: parsedFilters.filterArray },
                filterCount: { $set: parsedFilters.filterCount },
                status: { $set: 'updated-filters' }
            });
        }
        case 'update-lozenge-filters': {
            const newState = update(state, { filters: { $set: action.payload } });
            const parsedFilters = parseFilters(newState.filters);
            newState.filterString = parsedFilters.filterArray;
            newState.filterCount = parsedFilters.filterCount;
            newState.status = 'updated-filters';
            return newState;
        }
        case 'reset-filters':
            return update(state, {
                filters: { $set: initialState.filters },
                filterString: { $set: initialState.filterString },
                filterCount: { $set: initialState.filterCount },
                status: { $set: 'updated-filters' }
            });
        case 'search-starting':
            return {
                ...state,
                status: 'searching'
            };
        case 'bookmarks-loading':
            return {
                ...state,
                status: 'searching'
            };
        case 'search-complete': {
            if (state.status !== 'searching') return state;
            const sort = (QST.getSearchItem('sort') as string) || defaultSort;
            return update(state, {
                list: { $set: addIndex(action.payload) },
                listLength: { $set: action.payload.length },
                status: { $set: 'loaded-results' },
                sort: { $set: sort }
            });
        }
        case 'refined-search-complete': {
            if (state.status !== 'searching') return state;
            const sort = (QST.getSearchItem('sort') as string) || 'relevance';
            return update(state, {
                list: { $set: addIndex(action.payload) },
                listLength: { $set: action.payload.length },
                status: { $set: 'loaded-results' },
                sort: { $set: sort }
            });
        }
        case 'rehydrate-state': {
            const search = action.payload.search || '';
            const sort = action.payload.sort || defaultSort;
            const filters = action.payload.filters || initialState.filters;
            const parsedFilters = parseFilters(filters);
            return update(state, {
                search: { $set: search },
                sort: { $set: sort },
                filters: { $set: filters },
                filterString: { $set: parsedFilters.filterArray },
                filterCount: { $set: parsedFilters.filterCount },
                status: { $set: 'rehydrating' }
            });
        }
        default:
            throw new Error();
    }
};

const logGAevent = (search: string, type: SearchProductType, resultsCount: number) => {
    const filters = (QST.getSearchItem('filters') as string) || '';
    let searchContext = '';
    if (type === 'combinedProducts') searchContext = '';
    if (type === 'querylibrary') searchContext = 'Query Library';
    if (search && filters) {
        Window.dataLayer.push({
            event: 'searchSubmit',
            searchTerm: search,
            searchContext,
            searchFilters: filters,
            searchResultsCount: resultsCount
        });
    } else if (search) {
        Window.dataLayer.push({
            event: 'searchSubmit',
            searchContext,
            searchTerm: search,
            searchFilters: undefined,
            searchResultsCount: resultsCount
        });
    } else {
        Window.dataLayer.push({
            event: 'searchSubmit',
            searchContext,
            searchFilters: filters,
            searchTerm: undefined,
            searchResultsCount: resultsCount
        });
    }
};

const searchProvider = function SearchProvider(props: SearchProviderProps) {

    const { filters, render, bookmarks, type, urlPath, queryFilter, bookmarksLoaded, filterType, activeTab, updateSearchType } = props;
    const [state, dispatch] = React.useReducer(reducer, { ...initialState, filters });
    const user = useSelector((rootState: RootStoreState) => get(rootState, 'user', ''));
    const enabledBlueprintFlag = useSelector((stateRS: RootStoreState) => get(stateRS, 'shared.flags.enabledBlueprintFlag', false));

    const userEmail = user && user.userInfo && user.userInfo.Email ? user.userInfo.Email : '';
    const response: SearchProviderResponse = initialResponse;

    const [isLLMSearch, setIsLLMSearch] = React.useState<boolean>(false);
    const [genAISubmit, setGenAISubmit] = React.useState<boolean>(false);
    const [genAIResponse, setGenAIResponse] = React.useState<any>();
    const [hasGenAISearchError, setHasGenAISearchError] = React.useState<boolean>(false);
    const [feedback, setFeedback] = React.useState<string>('');
    const [clearFeedbackFlag, setClearFeedbackFlag] = React.useState<boolean>(false);
    const isLoggedIn = user && user.isLoggedIn ? user.isLoggedIn : false;
    const isValidUser = user ? validInternalUser(user) : false;
    const tileNameList = useSelector((stateRS: RootStoreState) => get(stateRS, 'shared.tileNamesGt25Chars', {}));
    const tileNamesGt25CharsList = tileNameList && tileNameList.data && tileNameList.data.TileNames ? tileNameList.data.TileNames : [];

    const { getSearchStrategy } = createSearchStrategyFactory();

    const decide = (searchText: string) => {
        if (type !== 'combinedProducts') return SearchType.openSearch;
        const searchStrategy = getSearchStrategy(searchText.trim(), tileNamesGt25CharsList);
        if (searchStrategy) return searchStrategy.apply();
        return SearchType.openSearch;
    };

    const getSearchType = (value: string, isDefault: boolean) => {
        setIsLLMSearch(false);
        const searchText = isDefault ? state.search : value;
        if (searchText && searchText.trim() && searchText.trim().length > 0) {
            const searchtype = decide(searchText);
            if (searchtype === SearchType.llmSearch) setIsLLMSearch(true);
            return searchtype;
        }
        return SearchType.openSearch;
    };

    // Example success callback function
    const handleSuccess = () => {
        console.log('Email request succeeded');
    };

    // Example error callback function
    const handleEmailError = (errorMessage: any) => {
        console.error(`Email request failed: ${errorMessage}`);
    };

    const emailStatusCallback = (hasRequestSuccess: boolean, successCallback: () => void, error: (error: string) => void) => {
        if (hasRequestSuccess) {
            successCallback();
        } else {
            error('Email request  failed');
        }
    };

    const sendEmail = (errorBody: string, errorSubject: string, includeKensho: boolean) => {
        APIManager.sendEmail(
            errorBody,
            errorSubject,
            includeKensho ? spgVariables.KENSHOLLMEmailList : spgVariables.LLMEmailList,
            (isSuccess: boolean) => {
                emailStatusCallback(isSuccess, handleSuccess, handleEmailError);
            }
        );
    };

    const sendSearchFailedEmail = (value: string, reason: any, includeKensho: boolean) => {
        const errorData = '<br>SearchValue - '.concat(value);
        const errorText = '<br>Reason - '.concat(JSON.stringify(reason));
        const errorBody = 'Gen AI Search service error: '.concat(errorData).concat(errorText);
        const errorSubject = (env ? env.toUpperCase().concat(' - ') : '').concat('Marketplace Gen AI Search Service Error');
        sendEmail(errorBody, errorSubject, includeKensho);
    };

    const getQueryData = () => {
        let userType;
        if (!isLoggedIn) {
            userType = 'not_authenticated';
        } else if (isValidUser) {
            userType = 'internal';
        } else {
            userType = 'external';
        }
        const data = {
            query: state.search,
            /* eslint-disable @typescript-eslint/camelcase */
            user_type: userType
        };
        return data;
    };

    const handleGenAIError = (isRequestManuallyCanceled = false) => {
        setGenAISubmit(false);
        if (!isRequestManuallyCanceled) {
            setHasGenAISearchError(true);
            setGenAIResponse([]);
        }
    };

    const onGenAISearchClick = () => {
        if (state.search) {
            const data = getQueryData();
            Window.dataLayer.push({
                event: 'globalSearchSubmitLLM',
                searchQuestion: data.query,
            });
            APIManager.getLLMAccessToken().then(token => {
                APIManager.getChatiqSearchById(data, token && token.data && token.data.AccessToken ? token.data.AccessToken : '').then(res => {
                    setGenAISubmit(false);
                    if (res.data) {
                        if (res.data.response_uuid && res.data.response) {
                            setGenAIResponse(res.data);
                            setHasGenAISearchError(false);
                        } else {
                            setHasGenAISearchError(true);
                            setGenAIResponse([]);
                            sendSearchFailedEmail(state.search, res.data, true);
                        }
                    }
                }).catch(error => {
                    const isCanceled = axios.isCancel(error);
                    handleGenAIError(isCanceled);
                    if (!isCanceled) sendSearchFailedEmail(state.search, error && error.message ? error.message : 'Search Service Error', true);
                });
            }).catch(er => {
                const isReqCanceled = axios.isCancel(er);
                handleGenAIError(isReqCanceled);
                if (!isReqCanceled) sendSearchFailedEmail(state.search, 'LLM Access Token Service Error', false);
            });
        }
    };

    const sendFeedbackFailedEmail = (data: any, reason: any, includeKensho: boolean) => {
        console.log('Gen AI Feedback service error, data=', JSON.stringify(data));
        const errorData = '<br>Data - '.concat(JSON.stringify(data));
        const errorText = '<br>Reason - '.concat(JSON.stringify(reason));
        const errorBody = 'Gen AI Feedback service error:'.concat(errorData).concat(errorText);
        const errorSubject = (env ? env.toUpperCase().concat(' - ') : '').concat('Marketplace Gen AI Feedback Service Error');
        sendEmail(errorBody, errorSubject, includeKensho);
    };

    response.handleFeedback = (id: string, flag: string) => {
        setFeedback(flag);
        const data = {
            response_uuid: id,
            feedback_flag: flag
        };
        APIManager.getLLMAccessToken().then(token => {
            APIManager.sendChatiqSearchFeedbackById(data, token && token.data && token.data.AccessToken ? token.data.AccessToken : '').then(res => {
                if (!(res.status === 200 && res.data === '')) {
                    sendFeedbackFailedEmail(data, res.data, true);
                }
            }).catch(error => {
                sendFeedbackFailedEmail(data, error && error.message ? error.message : 'Feedback Service Error', true);
            });
        }).catch(() => {
            sendFeedbackFailedEmail(data, 'LLM Access Token Service Error', false);
            console.log('Gen AI Feedback service access token error, data=', JSON.stringify(data));
        });
    };

    response.clearFeedbackDoneNotify = () => {
        setClearFeedbackFlag(false);
    };

    response.onTabPressed = (value: string) => {
        let d;
        if (value === 'gen-ai-result-end') {
            d = document.getElementById('kenshoIcon');
        }
        if (d && isLLMSearch) d.focus();
    };

    const resetGenAIAttribute = () => {
        setIsLLMSearch(false);
        setGenAISubmit(false);
        setGenAIResponse(null);
        setHasGenAISearchError(false);
        setClearFeedbackFlag(false);
        setFeedback('');
    };

    React.useEffect(() => {
        if (isLLMSearch) {
            dispatch({ type: 'search-complete', payload: [] });
            setGenAIResponse(null);
        }
        Store.dispatch({ flags: { hideFooterFlag: isLLMSearch }, type: SET_FLAGS });
        if (updateSearchType) updateSearchType(isLLMSearch);
    }, [isLLMSearch]);

    React.useEffect(() => {
        if (isLLMSearch && genAISubmit) {
            onGenAISearchClick();
        }
    }, [genAISubmit]);

    React.useEffect(() => {
        initialState.filters = filters;
        const hydratedState = hydrateStateFromUrl() as SearchProviderState;
        const hasBookmarkFilter =
            hydratedState.filters &&
            hydratedState.filters.some(f => f.filterKey === filterOptions.bookmark.filterKey && f.items.length > 0 && f.items[0].checked);
        if (hasBookmarkFilter && !bookmarksLoaded) {
            dispatch({ type: 'bookmarks-loading' });
        } else {
            dispatch({ type: 'rehydrate-state', payload: hydratedState });
        }
    }, [bookmarksLoaded, queryFilter, filterType]);

    React.useEffect(() => {
        if (state.status === 'rehydrating' || state.status === 'updated-search-debounced' || state.status === 'updated-filters') {
            dispatch({ type: 'search-starting' });

            const hasBookmarkFilter = state.filters.some(
                f => f.filterKey === filterOptions.bookmark.filterKey && f.items.length > 0 && f.items[0].checked
            );

            if (type === 'dataSets') {
                APIManager.search(state.search, state.filterString, ['isType:Dataset OR isType:Bundle']).then(res => {
                    let dataProfileCards = get(res, 'data.hits.hits', []);
                    dataProfileCards = dataProfileCards.map((item: Hit) => mapSearchResultToDataProfileCard(item._source));
                    if (hasBookmarkFilter) {
                        const dataSetIds = bookmarks.datasets.map(dataset => dataset.keyId.toString());
                        dataProfileCards = (dataProfileCards as DataProfileCard[]).filter(dataset => dataSetIds.includes(dataset.id.toString()));
                    }
                    if (state.search.length > 0 || state.filterString.length > 0) {
                        logGAevent(state.search, type, dataProfileCards.length);
                        dispatch({ type: 'refined-search-complete', payload: dataProfileCards });
                    } else {
                        dispatch({ type: 'search-complete', payload: dataProfileCards });
                    }
                }).catch(error => {
                    if (!(error && error.message && error.message === 'Search canceled due to new request.')) {
                        console.warn(error);
                    }
                });
            } else if (type === 'solutions') {
                APIManager.search(state.search, state.filterString, ['isType:Solution']).then(res => {
                    let solutionCards = get(res, 'data.hits.hits', []);
                    solutionCards = solutionCards.map((item: Hit) => mapSearchResultToSolution(item._source));
                    if (hasBookmarkFilter) {
                        const solutionIds = bookmarks.solutions.map(solution => solution.keyId.toString());
                        solutionCards = (solutionCards as APISolution[]).filter(solution => solutionIds.includes(solution.KeyPlatformService.toString()));
                    }
                    if (state.search.length > 0 || state.filterString.length > 0) {
                        logGAevent(state.search, type, solutionCards.length);
                        dispatch({ type: 'refined-search-complete', payload: solutionCards });
                    } else {
                        dispatch({ type: 'search-complete', payload: solutionCards });
                    }
                }).catch(error => {
                    if (!(error && error.message && error.message === 'Search canceled due to new request.')) {
                        console.warn(error);
                    }
                });
            } else if (type === 'combinedProducts') {
                const searchtype = getSearchType('', true);
                if (updateSearchType) updateSearchType(searchtype === SearchType.llmSearch);
                if (searchtype === SearchType.openSearch) {
                    resetGenAIAttribute();
                    APIManager.search(state.search, state.filterString, undefined, queryFilter).then(res => {
                        let data = get(res, 'data.hits.hits', []).map((hit: Hit) => {
                            if (hit._source.isType === 'Solution') {
                                return mapSearchResultToSolution(hit._source);
                            }
                            if (hit._source.isType === 'Blueprint') {
                                return mapSearchResultToBluePrint(hit._source);
                            }
                            return mapSearchResultToDataProfileCard(hit._source);
                        });
                        if (!enabledBlueprintFlag) {
                            data = data.filter((x: any) => x.KeyMarketplaceBlueprint === undefined);
                        }
                        if (hasBookmarkFilter) {
                            if (filterType === 'datasets') {
                                const dataSetIds = bookmarks.datasets.map(dataset => dataset.keyId.toString());
                                data = (data as DataProfileCard[]).filter(dataset => 'id' in dataset)
                                    .filter(dataset => dataSetIds.includes(dataset.id.toString()));
                            } else {
                                const solutionIds = bookmarks.solutions.map(solution => solution.keyId.toString());
                                data = (data as APISolution[]).filter(solution => 'KeyPlatformService' in solution)
                                    .filter(solution => solutionIds.includes(solution.KeyPlatformService));
                            }
                        }
                        if (state.search.length > 0 || state.filterString.length > 0) {
                            logGAevent(state.search, type, data.length);
                            dispatch({ type: 'refined-search-complete', payload: data });
                        } else {
                            dispatch({ type: 'search-complete', payload: data });
                        }
                    });
                } else if (searchtype === SearchType.llmSearch) {
                    setIsLLMSearch(true);
                    if (state.status === 'rehydrating') setGenAISubmit(true);
                }
            } else if (type === 'querylibrary') {
                APIManager.search(state.search, state.filterString, ['isType:Query']).then(res => {
                    let searchResult = get(res, 'data.hits.hits', []);
                    searchResult = searchResult.map((item: Hit) => mapSearchResultsToQuery(item._source));
                    if (hasBookmarkFilter) {
                        const queryIds = bookmarks.queries ? bookmarks.queries.map(queries => queries.keyId.toString()) : [];
                        searchResult = (searchResult as Query[]).filter(qry => queryIds.includes(qry.queryId.toString()));
                    }
                    if (state.search.length > 0 || state.filterString.length > 0) {
                        logGAevent(state.search, type, searchResult.length);
                        dispatch({ type: 'refined-search-complete', payload: searchResult });
                    } else {
                        dispatch({ type: 'search-complete', payload: searchResult });
                    }
                });
            } else if (type === 'blueprints') {
                APIManager.search(state.search, state.filterString, ['isType:Blueprint']).then(res => {
                    let bluePrintCards = get(res, 'data.hits.hits', []);
                    bluePrintCards = bluePrintCards.map((item: Hit) => mapSearchResultToBluePrint(item._source));
                    if (hasBookmarkFilter) {
                        const bluePrintIds = bookmarks.blueprints && bookmarks.blueprints.map(blueprint => blueprint.keyId.toString());
                        bluePrintCards = (bluePrintCards as APIBlueprint[]).filter(blueprint => bluePrintIds && bluePrintIds.includes(blueprint.KeyMarketplaceBlueprint.toString()));
                    }
                    if (state.search.length > 0 || state.filterString.length > 0) {
                        logGAevent(state.search, type, bluePrintCards.length);
                        dispatch({ type: 'refined-search-complete', payload: bluePrintCards });
                    } else {
                        dispatch({ type: 'search-complete', payload: bluePrintCards });
                    }
                }).catch(error => {
                    if (!(error && error.message && error.message === 'Search canceled due to new request.')) {
                        console.warn(error);
                    }
                });
            }
        }
        const historyListener = HistoryInstance.listen((location, action) => {
            if (location.pathname === urlPath) {
                const newSearch = QST.getSearchItem('search') || '';
                const newFilters = QST.getSearchItem('filters') || '';
                // if action = POP (back) or is refresh (click top menu) then rehydrate!
                const isReset = action === 'PUSH' && newSearch === '' && newFilters === '';
                const isNewSearch = action === 'PUSH' && newSearch !== state.search;
                if (action === 'POP' || isReset || isNewSearch) {
                    dispatch({ type: 'rehydrate-state', payload: hydrateStateFromUrl() });
                }
            }
        });
        return () => {
            historyListener();
        };
    }, [state.status, state.search]);

    const resetFilters = () => {
        if (state.filterCount === 0) return;
        dispatch({ type: 'reset-filters' });
        QST.removeSearchItem('filters');
    };

    response.selectComponent = selectComponent(type, state.sort, state.listLength < 1, (name: string, value: string) => {
        dispatch({ type: 'update-sort', payload: value });
        updateSortUrl(value);
    });

    response.searchComponent = searchComponent(type, activeTab || 0, isLLMSearch ? [] : state.list, state.search, state.status === 'searching', (value: string) => {
        const payload = value.replace(/ {2,}/g, ' ');
        const searchType = getSearchType(payload, false);
        if (updateSearchType) updateSearchType(searchType === SearchType.llmSearch);
        dispatch({ type: 'update-search', payload });
        if (hasGenAISearchError) setHasGenAISearchError(false);
        if (searchType === SearchType.llmSearch && genAIResponse && feedback !== '') {
            setClearFeedbackFlag(true);
            setFeedback('');
        }
        updateSearchUrl(payload, () => dispatch({ type: 'update-search-debounced' }));
    }, (val: number) => {
        if (resetFilters) resetFilters();
        QST.setSearchItem({ tab: val }, true);
    },
    urlPath, isLLMSearch,
    (isSubmit: boolean) => {
        const value = getSearchType('', true);
        if (value === SearchType.llmSearch && isSubmit) {
            setGenAISubmit(isSubmit);
        }
    }, genAISubmit, genAIResponse);

    response.filterComponent = filterComponent(
        state.filters,
        resetFilters,
        state.filterCount > 0,
        (filterItems: FilterCheckBoxItem[], filterKey: string) => {
            dispatch({ type: 'update-filter', payload: { filterItems, filterKey } });

            updateFilterURL(filterItems, filterKey);
        }
    );

    response.lozengeFilterComponent = lozengeFilterComponent(state.filters, (filterItems: FilterObjects[]) => {
        dispatch({ type: 'update-lozenge-filters', payload: filterItems });
        updateFiltersURL(filterItems);
    });

    response.mobileSelectComponent = mobileSelectComponent(type, state.sort, state.listLength < 1, (name: string, value: string) => {
        dispatch({ type: 'update-sort', payload: value });
        updateSortUrl(value);
    });

    response.mobileFilterComponent = mobileFilterComponent(state.filters, resetFilters, state.filterCount, (newFilters: FilterObjects[]) => {
        dispatch({ type: 'update-filters', payload: newFilters });
        updateFiltersURL(newFilters);
    });

    return (
        <>
            {render({
                ...response,
                sort: state.sort,
                search: state.search,
                isSearching: isLLMSearch ? isLLMSearch && genAISubmit : state.status === 'searching' || state.status === 'initial-load' || state.status === 'rehydrating',
                listLength: state.listLength,
                list: authdata(sortedList(state.sort, type, state.list), userEmail),
                filterCount: state.filterCount,
                status: state.status,
                resetFilters,
                isLLMSearch,
                genAIResponse,
                hasGenAISearchError,
                clearFeedbackFlag
            })}
        </>
    );
};

export default React.memo(searchProvider);
